<template>
	<div
		v-if="!loadingTestAndContinue"
		class="block-editor-bottom-bar width-100 flex-centerize"
		:class="{ succ: isSuccessResultStatus, failed: isErroredResultsStatus, skipped: isSkippedResultStatus }"
	>
		<div v-if="!$slots.content" class="bottom-bar-content">
			<div v-if="!isSuccessResultStatus && !testResponse.error && !isSkippedResultStatus" class="left-side-dummy-div" />

			<div class="left-side" v-if="isSuccessResultStatus || isErroredResultsStatus || isSkippedResultStatus">
				<div v-if="isSuccessResultStatus">
					<IconSuccess />
					<div>Test successful</div>
				</div>
				<div v-if="isErroredResultsStatus">
					<IconError />
					<div>Test failed</div>
					<div class="link-style subtle ml-2" @click="seeExampleData">see details</div>
				</div>
				<div v-if="isSkippedResultStatus">
					<SIcon name="back-test" />
					<div>Test skipped</div>
				</div>
			</div>

			<v-btn v-if="seeSampleButtonShown" class="btn-example-data" @click="seeExampleData"> see sample data </v-btn>
			<span v-else-if="isWebhookNode">Send webhook data to see results</span>
			<!-- <span v-else-if="triggerConfigured">Trigger the event to see results</span> -->
			<div v-else-if="!isSuccessResultStatus" class="flex-centerize">
				<v-btn class="flex-1 btn btn-primary btn-lg" :loading="btnFuncLoading" :disabled="btnDisabled" @click="btnFunc">
					{{ btnTextInput || btnText }}
				</v-btn>
			</div>

			<div class="flex-end--center skip-text" :class="[showSkipTest ? 'show' : 'display-none']">
				<div class="link-style subtle" @click="skipTest">skip test</div>
			</div>

			<div v-if="!isSuccessResultStatus && !showSkipTest" class="right-side-dummy-div" />

			<v-slide-y-reverse-transition leave-absolute mode="out-in">
				<div v-if="loadingTestAndContinue">
					<div class="text-center">loading..</div>
				</div>
			</v-slide-y-reverse-transition>
		</div>
		<slot name="content"> </slot>
	</div>
</template>

<script>
import { sanitizeHtml } from '@/utils/helpers';

import { mapGetters } from 'vuex';
import { isStatusPublishValid } from './block-editor-service';
import { mapGettersShorthand } from '@/store/utils/mappers';
import { isNullOrUndefinedOrEmpty } from '@/utils/helpers';
import IconError from '../FlowTreeBlock/assets/icon-x.svg';
import IconSuccess from '../FlowTreeBlock/assets/icon-vi.svg';
import { TEST_RESULT_STATUSES } from '@/utils/workflow-node.const';
import { isSendNotification, isWebhook, isTriggerNode } from '@/store/modules/selectedNode/getters';
import { rootNodeId as rootNodeIdGetter } from '@/store/modules/workflows/getters';

export default {
	name: 'BlockEditorBottomBar',
	components: {
		IconSuccess,
		IconError,
	},
	props: {
		testResultStatus: { type: String },
		triggerConfig: { type: Object, default: null },
		btnTextInput: { type: String, default: '' },
		testResponse: { type: Object, default: null },
		loadingTestAndContinue: { type: Boolean, default: false },
		showTestResultModal: { type: Boolean, default: false },
		btnDisabled: { type: Boolean, default: false },
		showParams: { type: Boolean, default: false },
		btnFuncLoading: { type: Boolean, default: false },
		isPreventSkip: { type: Boolean },
		isCloudFlow: { type: Boolean, default: false },
		hasEditPermissions: { type: Boolean, default: false },
	},
	computed: {
		...mapGetters('selectedNode', [
			'nodeType',
			'isBranches',
			'editNode',
			'isBranchesConsolidateDataNode',
			'isWebhook',
			'isScheduledWebhook',
		]),
		...mapGettersShorthand({ isSendNotification, isWebhook, isTriggerNode, rootNodeIdGetter }),
		isShowTestResults() {
			const hasTestResponse = !(isNullOrUndefinedOrEmpty(this.testResponse) || Object.keys(this.testResponse).length === 0);
			return (
				(hasTestResponse || this.isErroredResultsStatus || this.isSuccessResultStatus || this.isSkippedResultStatus) &&
				!this.showTestResultModal
			);
		},
		isSuccessResultStatus() {
			return this.testResultStatus === TEST_RESULT_STATUSES.SUCCESS;
		},
		isErroredResultsStatus() {
			return this.testResultStatus === TEST_RESULT_STATUSES.ERROR;
		},
		isSkippedResultStatus() {
			return this.testResultStatus === TEST_RESULT_STATUSES.SKIPPED;
		},
		isShowExistingTestResults() {
			return !this.showTestResultModal && !isNullOrUndefinedOrEmpty(this.testResponse) && !this.isSkippedResultStatus;
		},
		btnText() {
			if (this.isSendNotification) {
				return 'Test notification';
			}

			if (this.isTriggerNode && this.isCloudFlow) {
				// Here we are in automation, with trigger node
				if (this.showParams || this.isScheduledWebhook) {
					return 'Test trigger';
				}
				return 'Continue';
			}

			if (this.showParams || this.isBranches || this.isBranchesConsolidateDataNode || this.isWebhook) {
				return this.isErroredResultsStatus ? 'Test again' : 'Test step';
			}
			return 'Continue';
		},
		seeSampleButtonShown() {
			return this.isSuccessResultStatus && this.isShowExistingTestResults;
		},
		isWebhookNode() {
			return this.isWebhook && this.isCloudFlow && this.isRootNode;
		},
		rootNodeId() {
			return this.rootNodeIdGetter(this.editNode.workflow, this.editNode.workflow_version);
		},
		isRootNode() {
			return this.editNode.id === this.rootNodeId;
		},
		triggerConfigured() {
			return this.isTriggerNode && this.triggerConfig?.subscribed;
		},
		isStatusPublishValid() {
			return isStatusPublishValid(this.testResultStatus);
		},
		showSkipTest() {
			const { isPreventSkip, isSendNotification, isStatusPublishValid, isWebhookNode, hasEditPermissions, btnDisabled } = this;
			return !isPreventSkip && !isSendNotification && !isStatusPublishValid && !isWebhookNode && hasEditPermissions && !btnDisabled;
		},
	},
	methods: {
		skipTest() {
			this.$emit('skipTest');
		},
		btnFunc() {
			this.$emit('btnFunc');
		},
		seeExampleData() {
			this.$emit('update:showTestResultModal', true);
			this.$emit('update:isTestingWidget', false);
		},
		getCleanHtml(dirtyContent) {
			return sanitizeHtml(String(dirtyContent));
		},
	},
};
</script>

<style scoped lang="scss">
.block-editor-bottom-bar {
	position: relative;
	min-height: $bottom-bar-height;
	width: 100%;
	padding: 0 2%;
	transform: translateY(0px);
	z-index: 33;
	background: white !important;

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		content: '';
		box-shadow: 0 -4px 5px rgba(0, 0, 0, 0.2);
		z-index: 99999999;
		height: 5px;
	}

	.btn-example-data {
		max-width: 140px;
		margin: 2px;
		font-size: 13px !important;
		background-color: transparent !important;
		color: white;
		border: 1px solid white;
	}

	.btn-primary {
		padding: 20px;
		color: white;
		font-size: 14px !important;
		font-weight: 500 !important;
		letter-spacing: 0em !important;
		max-width: 200px !important;
	}

	&.failed {
		color: #e94e4e !important;
	}
	&.succ {
		color: white;
		background-color: #59c4a1 !important;

		svg {
			circle {
				stroke: white;
			}
			path {
				fill: white;
			}
		}

		i {
			color: #22be9f !important;
		}
	}
}

.bottom-bar-content {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex: 1;
	font-size: 12px;

	> * {
		flex: 1;
	}

	svg,
	::v-deep svg {
		margin-right: 4px;
	}

	.left-side {
		display: flex;
		align-items: center;
		justify-content: flex-start;

		> div {
			display: flex;
			align-items: center;
			justify-content: flex-start;
		}
	}

	button {
		max-width: 130px;
	}
}
.skip-text {
	opacity: 0;
	&.show {
		opacity: 1;
	}
}
</style>
